import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-[#012d5c] mb-4 pb-2 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const Terms = () => {
  useEffect(() => {
    document.title = "Terms and Conditions | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Read the Integro Clinics Terms and Conditions. Understand the terms governing your use of our website and clinical services.";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Legal
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Terms and Conditions
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Please read these terms carefully before using our website or clinical services.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            <Section title="Introduction">
              <p>
                These Terms and Conditions govern your use of the Integro Clinics website and clinical services. By accessing our website or registering for our services, you agree to be bound by these terms.
              </p>
              <p>
                Integro Clinics provides specialist clinical assessments and, where clinically approved, access to medical cannabis treatment. We are registered with the Care Quality Commission (CQC) and operate fully within applicable UK healthcare regulations.
              </p>
              <p>
                We may update these terms from time to time. Continued use of our services following any update constitutes acceptance of the revised terms.
              </p>
            </Section>

            <Section title="Services">
              <p>
                Integro Clinics provides online clinical assessments via secure video consultation. Our services are provided to adults aged 21 and over who meet our eligibility criteria.
              </p>
              <p>
                Our services are healthcare services, not a general retail offering. We do not guarantee access to treatment, prescriptions or any specific clinical outcome. All clinical decisions are made by our specialist clinical team following thorough assessment and independent MDT review.
              </p>
              <p>
                We reserve the right to decline or withdraw services where clinically or administratively appropriate.
              </p>
            </Section>

            <Section title="Clinical Assessment and Prescribing">
              <p>
                A clinical assessment involves a specialist clinician reviewing your medical history and current condition. Assessment outcomes are clinical decisions made in the best interests of the individual patient.
              </p>
              <p>
                <strong>A prescription is not guaranteed following a consultation.</strong> Prescribing decisions are made independently by our multi-disciplinary team (MDT) following thorough review of all available clinical information. The MDT may determine that medical cannabis treatment is not appropriate for a patient, and in those circumstances no prescription will be issued.
              </p>
              <p>
                Completing an eligibility check or registration does not guarantee a consultation, and completing a consultation does not guarantee a prescription.
              </p>
            </Section>

            <Section title="Prescription Policy">
              <p>
                Where the MDT approves a prescription, it will be issued electronically to a regulated specialist pharmacy. Prescriptions are dispensed and fulfilled by the pharmacy in accordance with applicable regulations.
              </p>
              <p>
                Integro Clinics is not a pharmacy. We are not responsible for the dispensing, supply or delivery of medications, which are handled by regulated third-party pharmacies. Any queries about dispensed medication should be directed to the dispensing pharmacy.
              </p>
              <p>
                A flat admin fee of £4.99 applies per prescription processed. Clinical consultations, follow-up appointments and repeat prescription reviews are provided at no charge.
              </p>
            </Section>

            <Section title="Refunds, Returns and Cancellations">
              <p>
                Clinical consultation services are provided at no charge and therefore no consultation refund applies.
              </p>
              <p>
                The prescription admin fee (£4.99) is non-refundable once a prescription has been processed, unless there is an error on our part.
              </p>
              <p>
                If you have concerns about a charge, please contact us at{" "}
                <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                  office@integroclinics.com
                </a>{" "}
                within 7 days of the transaction date.
              </p>
              <p>
                Queries relating to medication already dispensed by a pharmacy should be raised directly with the dispensing pharmacy.
              </p>
            </Section>

            <Section title="Disputes and Complaints">
              <p>
                If you are dissatisfied with any aspect of our service, we encourage you to contact us in writing as soon as possible. Please see our{" "}
                <a href="/complaints" className="text-[#005eb8] hover:underline">
                  Complaints Policy
                </a>{" "}
                for full details of our complaints process.
              </p>
              <p>
                Written complaints should be submitted to{" "}
                <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                  office@integroclinics.com
                </a>
                . We will acknowledge complaints within 5 working days and aim to provide a full response within 28 days.
              </p>
            </Section>

            <Section title="Data Protection and Confidentiality">
              <p>
                We are committed to protecting your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Please refer to our{" "}
                <a href="/privacy-policy" className="text-[#005eb8] hover:underline">
                  Privacy Policy
                </a>{" "}
                for full details of how we collect, use and protect your data.
              </p>
              <p>
                All patient information is treated as confidential and is only shared where clinically necessary or required by law.
              </p>
            </Section>

            <Section title="Acceptable Use">
              <p>You agree not to:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Use our website or services for any unlawful purpose.",
                  "Submit false, misleading or fraudulent information.",
                  "Attempt to gain unauthorised access to our systems or patient records.",
                  "Use our website in any way that could damage, disable or impair it.",
                  "Reproduce, distribute or transmit any content from our website without permission.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Intellectual Property">
              <p>
                All content on this website, including text, images, logos and design, is the intellectual property of Integro Clinics or its licensors and is protected by applicable copyright and intellectual property laws.
              </p>
              <p>
                You may view and print content from this website for personal, non-commercial use only. You may not reproduce, redistribute or exploit any content for commercial purposes without our express written permission.
              </p>
            </Section>

            <Section title="Liability">
              <p>
                Integro Clinics provides general health information on this website for educational purposes. This information does not constitute medical advice and should not be relied upon as a substitute for professional clinical guidance.
              </p>
              <p>
                To the fullest extent permitted by law, Integro Clinics will not be liable for any indirect, consequential or incidental loss arising from your use of our website or services. Our liability in respect of any direct loss shall not exceed the amount you have paid to us for services within the preceding twelve months.
              </p>
              <p>
                Nothing in these terms excludes or limits our liability for death or personal injury caused by negligence, fraud, or any other matter that cannot be limited by law.
              </p>
            </Section>

            <Section title="Changes to the Service">
              <p>
                We reserve the right to modify, suspend or discontinue any aspect of our services at any time. We will endeavour to provide reasonable notice of any significant changes where practicable.
              </p>
            </Section>

            <Section title="Service Suspension and Termination">
              <p>
                We may suspend or terminate your access to our services if you breach these terms or engage in behaviour that is harmful to staff, other patients or the integrity of our service. In such cases, we will advise you to seek care through an alternative provider.
              </p>
            </Section>

            <Section title="Access to the Website and Application">
              <p>
                We do not guarantee that our website or patient portal will always be available or error-free. We may carry out maintenance or updates that temporarily affect access. We are not liable for any loss resulting from unavailability of the website or portal.
              </p>
            </Section>

            <Section title="Governing Law">
              <p>
                These Terms and Conditions are governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </Section>

            <Section title="Contact Us">
              <p>
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="mt-4 bg-[#f8fafc] border border-gray-100 rounded-xl p-6 space-y-2">
                <p><strong>Email:</strong>{" "}
                  <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                    office@integroclinics.com
                  </a>
                </p>
              </div>
            </Section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
